<template>
  <div id="app" class="relative flex h-dvh min-h-0 w-full flex-col overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(194,216,105,0.18),_transparent_42%),linear-gradient(180deg,_#f8faf7_0%,_#eef2f7_100%)] antialiased text-slate-800">
    <router-view v-slot="{ Component, route }">
      <Transition name="app-page" mode="out-in" appear>
        <component
          :is="Component"
          :key="getTopLevelRouteKey(route)"
          class="min-h-0 flex-1"
        />
      </Transition>
    </router-view>

    <Transition name="version-alert">
      <VersionAlert
        v-if="versionAlert.show"
        @closeAlert="closeAlert"
        @refresh="refreshApp"
        class="fixed inset-x-0 top-5 z-[999] mx-auto"
      />
    </Transition>

    <Notification />
  </div>
</template>

<script>
import VersionAlert from '@/components/VersionAlert.vue';
import Notification from '@/components/Notification.vue';
import appVersion from '@/app-version.json';

const VERSION_ACCEPTED_KEY = 'jg_app_version_accepted_build_id';
const VERSION_DISMISSED_KEY = 'jg_app_version_dismissed_build_id';
const VERSION_AUTO_RELOAD_PREFIX = 'jg_app_version_auto_reload_';

export default {
  name: 'App',

  components: {
    VersionAlert,
    Notification
  },

  data() {
    return {
      versionAlert: {
        show: false,
        currentBuildId: String(appVersion?.buildId || ''),
        latestBuildId: "",
        acceptedBuildId: "",
        dismissedBuildId: "",
      },
      versionCheckInterval: null,
      versionCheckInProgress: false,
    };
  },

  computed: {
    user() {
      return this.$store.state.user || null;
    },

    authIsReady() {
      return !!this.$store.state.authIsReady;
    }
  },

  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (!this.authIsReady || user) return;
        if (this.$route.path !== "/") {
          this.$router.replace("/");
        }
      }
    },
    authIsReady: {
      immediate: true,
      handler(ready) {
        if (!ready || this.user) return;
        if (this.$route.path !== "/") {
          this.$router.replace("/");
        }
      }
    },
    $route: {
      immediate: true,
      handler(to) {
        this.setMeta(to?.meta || {});
      }
    }
  },

  methods: {
    getStoredBuildId(key) {
      try {
        return String(localStorage.getItem(key) || '').trim();
      } catch (error) {
        console.log(error?.message || error);
        return "";
      }
    },

    setStoredBuildId(key, buildId) {
      try {
        if (buildId) localStorage.setItem(key, buildId);
      } catch (error) {
        console.log(error?.message || error);
      }
    },

    syncStoredVersionState() {
      this.versionAlert.acceptedBuildId = this.getStoredBuildId(VERSION_ACCEPTED_KEY);
      this.versionAlert.dismissedBuildId = this.getStoredBuildId(VERSION_DISMISSED_KEY);
    },

    isVersionAlreadyHandled(buildId) {
      return Boolean(
        buildId &&
        (buildId === this.versionAlert.acceptedBuildId ||
          buildId === this.versionAlert.dismissedBuildId)
      );
    },

    shouldAutoReloadToAcceptedBuild(buildId) {
      if (!buildId || buildId !== this.versionAlert.acceptedBuildId) return false;

      try {
        const reloadKey = `${VERSION_AUTO_RELOAD_PREFIX}${buildId}`;
        if (sessionStorage.getItem(reloadKey) === "1") return false;
        sessionStorage.setItem(reloadKey, "1");
        return true;
      } catch (error) {
        console.log(error?.message || error);
        return true;
      }
    },

    closeAlert() {
      this.versionAlert.show = false;
      this.versionAlert.dismissedBuildId = this.versionAlert.latestBuildId;
      this.setStoredBuildId(VERSION_DISMISSED_KEY, this.versionAlert.latestBuildId);
    },

    setMeta(meta = {}) {
      document.title = meta.pageTitle || 'Panel - Jawgarden';
    },

    getTopLevelRouteKey(route) {
      return route?.matched?.[0]?.path || route?.path || 'app-view';
    },

    async fetchAppVersion() {
      const response = await fetch(`/app-version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Version check failed: ${response.status}`);
      }

      return response.json();
    },

    async checkAppVersion() {
      if (this.versionCheckInProgress) return;

      try {
        this.versionCheckInProgress = true;
        this.syncStoredVersionState();
        const latest = await this.fetchAppVersion();
        const latestBuildId = String(latest?.buildId || '').trim();

        if (!latestBuildId) return;

        if (!this.versionAlert.currentBuildId) {
          this.versionAlert.currentBuildId = latestBuildId;
          return;
        }

        if (
          latestBuildId !== this.versionAlert.currentBuildId &&
          this.shouldAutoReloadToAcceptedBuild(latestBuildId)
        ) {
          this.versionAlert.show = false;
          window.location.reload();
          return;
        }

        if (
          latestBuildId !== this.versionAlert.currentBuildId &&
          !this.isVersionAlreadyHandled(latestBuildId)
        ) {
          this.versionAlert.latestBuildId = latestBuildId;
          this.versionAlert.show = true;
        } else if (latestBuildId === this.versionAlert.currentBuildId || this.isVersionAlreadyHandled(latestBuildId)) {
          this.versionAlert.show = false;
        }
      } catch (error) {
        console.log(error?.message || error);
      } finally {
        this.versionCheckInProgress = false;
      }
    },

    startVersionChecker() {
      this.checkAppVersion();

      this.versionCheckInterval = window.setInterval(() => {
        this.checkAppVersion();
      }, 60000);

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('focus', this.checkAppVersion);
      window.addEventListener('storage', this.handleVersionStorageChange);
    },

    stopVersionChecker() {
      if (this.versionCheckInterval) {
        window.clearInterval(this.versionCheckInterval);
        this.versionCheckInterval = null;
      }

      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('focus', this.checkAppVersion);
      window.removeEventListener('storage', this.handleVersionStorageChange);
    },

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.checkAppVersion();
      }
    },

    handleVersionStorageChange(event) {
      if (![VERSION_ACCEPTED_KEY, VERSION_DISMISSED_KEY].includes(event?.key)) return;

      this.syncStoredVersionState();

      if (
        this.versionAlert.latestBuildId &&
        this.versionAlert.latestBuildId !== this.versionAlert.currentBuildId &&
        this.shouldAutoReloadToAcceptedBuild(this.versionAlert.latestBuildId)
      ) {
        this.versionAlert.show = false;
        window.location.reload();
        return;
      }

      if (this.isVersionAlreadyHandled(this.versionAlert.latestBuildId)) {
        this.versionAlert.show = false;
      }
    },

    refreshApp() {
      const handledBuildId = this.versionAlert.latestBuildId || this.versionAlert.currentBuildId;
      this.versionAlert.acceptedBuildId = handledBuildId;
      this.setStoredBuildId(VERSION_ACCEPTED_KEY, handledBuildId);
      this.versionAlert.show = false;
      window.location.reload();
    },

    resolveApiLink() {
      const version = process.env.VUE_APP_VERSION;

      if (version === 'PRODUCTION_LOCAL') {
        return 'http://127.0.0.1:5001/jawgarden-4e611/europe-central2/api';
      }

      if (version === 'PRODUCTION') {
        return 'https://europe-central2-jawgarden-4e611.cloudfunctions.net/api';
      }

      if (version === 'SANDBOX_LOCAL') {
        return 'http://127.0.0.1:5001/jawgarden-sandbox/europe-central2/api';
      }

      if (version === 'SANDBOX') {
        return 'https://europe-central2-jawgarden-sandbox.cloudfunctions.net/api';
      }
    }
  },

  created() {
    const apiLink = this.resolveApiLink();
    this.$store.commit('setUrl', apiLink);
    console.log('VUE_APP_VERSION:', process.env.VUE_APP_VERSION);
  },

  mounted() {
    this.syncStoredVersionState();
    this.startVersionChecker();
  },

  beforeUnmount() {
    this.stopVersionChecker();
  }
};
</script>

<style lang="scss">
html,
body,
#app {
  height: 100%;
  margin: 0;
}

html,
body {
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

.fade-enter-active {
  transition: opacity 1s ease;
}

.fade-leave-active {
  transition: opacity 0s;
  display: none;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade2-enter-active,
.fade2-leave-active {
  transition: opacity 0.5s ease;
}

.fade2-enter-from,
.fade2-leave-to {
  opacity: 0;
}

.version-alert-enter-active {
  transition:
    opacity 260ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 360ms cubic-bezier(0.16, 1, 0.3, 1),
    filter 320ms cubic-bezier(0.16, 1, 0.3, 1);
}

.version-alert-leave-active {
  transition:
    opacity 140ms cubic-bezier(0.7, 0, 0.84, 0),
    transform 180ms cubic-bezier(0.7, 0, 0.84, 0),
    filter 140ms cubic-bezier(0.7, 0, 0.84, 0);
}

.version-alert-enter-from,
.version-alert-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.985);
  filter: blur(4px);
}

.version-alert-enter-to,
.version-alert-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
}

.app-page-enter-active {
  transition:
    opacity 180ms cubic-bezier(0.2, 0, 0, 1),
    transform 220ms cubic-bezier(0.2, 0, 0, 1),
    filter 220ms cubic-bezier(0.2, 0, 0, 1);
}

.app-page-leave-active {
  transition:
    opacity 130ms cubic-bezier(0.4, 0, 1, 1),
    transform 130ms cubic-bezier(0.4, 0, 1, 1),
    filter 130ms cubic-bezier(0.4, 0, 1, 1);
}

.app-page-enter-from {
  opacity: 0;
  transform: translateY(8px) scale(0.992);
  filter: blur(2px);
}

.app-page-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.996);
  filter: blur(1px);
}

.app-page-enter-to,
.app-page-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
}

@media (prefers-reduced-motion: reduce) {
  .app-page-enter-active,
  .app-page-leave-active,
  .version-alert-enter-active,
  .version-alert-leave-active {
    transition: opacity 1ms linear;
  }

  .app-page-enter-from,
  .app-page-leave-to,
  .version-alert-enter-from,
  .version-alert-leave-to {
    opacity: 0;
    transform: none;
    filter: none;
  }
}
</style>
